import { Controller } from "@hotwired/stimulus"

// A secret the page shows masked, in a key box or a password field. Show is
// a Turbo Stream link: the server answers by replacing the slot with the
// value, and slotTargetConnected puts it in place. A field that already
// holds what was typed shows it without asking. Hide masks it again (a key
// box drops it from the page); Copy copies what is shown. A field shows its
// tools only while it has a value: a saved one or what is typed, and the
// count of what it holds.
export default class extends Controller {
  static targets = ["field", "slot", "toggle", "copy", "copyLabel", "tools", "count"]
  static values = {
    masked: String,
    show: String,
    hide: String,
    characters: String,
    storedLength: Number,
    copied: { type: String, default: "✓" },
    revealed: Boolean,
  }

  connect() {
    this.sync()
  }

  sync() {
    if (this.hasToolsTarget) this.toolsTarget.hidden = !(this.typed || this.hasSlotTarget)

    if (!this.hasCountTarget) return

    const typed = this.isInput ? this.fieldTarget.value.length : 0
    const length = typed || (this.hasSlotTarget ? this.storedLengthValue : 0)

    this.countTarget.hidden = length === 0
    this.countTarget.textContent = this.charactersValue.replace("{count}", length)
  }

  toggle(event) {
    if (this.revealedValue) {
      event.preventDefault()
      this.conceal()

      return
    }

    if (this.typed || !this.toggleTarget.hasAttribute("href")) {
      event.preventDefault()
      this.reveal()
    }
  }

  slotTargetConnected(slot) {
    const secret = slot.dataset.revealedSecret

    if (secret === undefined) return

    delete slot.dataset.revealedSecret
    this.reveal(secret)
  }

  reveal(secret) {
    if (this.isInput) {
      if (secret !== undefined) this.fieldTarget.value = secret

      this.fieldTarget.type = "text"
      this.sync()
    } else {
      this.fieldTarget.textContent = secret
    }

    this.revealedValue = true
  }

  generate(event) {
    event.preventDefault()

    this.reveal(`whsec_${this.urlSafeBase64()}`)
  }

  // 33 bytes need no padding, and a secret without + or / reaches the query
  // string of a webhook as it is.
  urlSafeBase64() {
    let encoded

    do {
      encoded = btoa(String.fromCharCode(...crypto.getRandomValues(new Uint8Array(33))))
    } while (/[+/]/.test(encoded))

    return encoded
  }

  conceal() {
    if (this.isInput) {
      this.fieldTarget.type = "password"
    } else {
      this.fieldTarget.textContent = this.maskedValue
    }

    this.revealedValue = false
  }

  revealedValueChanged(revealed) {
    if (this.hasToggleTarget) this.label(this.toggleTarget, revealed ? this.hideValue : this.showValue, revealed)

    if (this.hasCopyTarget && !this.isInput) this.copyTarget.hidden = !revealed
  }

  copy() {
    const text = this.isInput || this.revealedValue ? this.text : ""

    if (!text) return

    navigator.clipboard.writeText(text).then(() => {
      if (!this.hasCopyLabelTarget) {
        this.copyTarget.dataset.copied = ""
        setTimeout(() => { delete this.copyTarget.dataset.copied }, 1500)

        return
      }

      const label = this.copyLabelTarget.textContent

      this.copyLabelTarget.textContent = this.copiedValue
      setTimeout(() => { this.copyLabelTarget.textContent = label }, 1500)
    })
  }

  label(button, text, pressed) {
    if (button.dataset.iconOnly === undefined) {
      button.textContent = text

      return
    }

    button.setAttribute("aria-label", text)
    button.setAttribute("title", text)
    button.setAttribute("aria-pressed", String(pressed))
  }

  get text() {
    return this.isInput ? this.fieldTarget.value : this.fieldTarget.textContent
  }

  get isInput() {
    return this.fieldTarget instanceof HTMLInputElement
  }

  get typed() {
    return this.isInput && this.fieldTarget.value !== ""
  }
}
